import { useState, useEffect, useRef, useCallback } from 'react';
import CreateExerciseForm from '../../../../components/CreateExerciseForm';
import type { ExerciseEditDrawerHandle } from '../../library/overlays/ExerciseEditDrawer';
import type { ExerciseCreateDrawerHandle } from '../../library/overlays/ExerciseCreateDrawer';
import { WorkoutService } from '../../../../lib/workoutService';
import { ExerciseService } from '../../../../lib/exerciseService';
import type { CreateExerciseData, Exercise, TrainerExerciseListRow } from '../../../../types/dataModel';
import type { useLibraryExercises } from './useLibraryExercises';

export type UseExerciseOverlayStackParams = {
  activeSection: string;
  activeTab: string;
  loadAllExercisesForLibrary: (options?: { silent?: boolean }) => Promise<void>;
  ex: ReturnType<typeof useLibraryExercises>;
};

/** Exercise drawers, companion form, edit-warning modal (P15-R3). */
export function useExerciseOverlayStack({
  activeSection,
  activeTab,
  loadAllExercisesForLibrary,
  ex,
}: UseExerciseOverlayStackParams) {
  const [createExerciseDrawerOpen, setCreateExerciseDrawerOpen] = useState(false);
  const [creatingExercise, setCreatingExercise] = useState(false);
  const [editingExercise, setEditingExercise] = useState<Exercise | null>(null);
  const [viewingExercise, setViewingExercise] = useState<Exercise | null>(null);
  const [drawerIsEdit, setDrawerIsEdit] = useState(false);
  const [exerciseTagOptions, setExerciseTagOptions] = useState<{ muscle: string[]; equipment: string[] }>({
    muscle: [],
    equipment: [],
  });
  const [savedToastExerciseName, setSavedToastExerciseName] = useState<string | null>(null);
  const [savedToastVariant, setSavedToastVariant] = useState<'edit' | 'create'>('edit');
  const exerciseEditDrawerRef = useRef<ExerciseEditDrawerHandle | null>(null);
  const createExerciseDrawerRef = useRef<ExerciseCreateDrawerHandle | null>(null);
  const [showExerciseEditWarningModal, setShowExerciseEditWarningModal] = useState(false);
  const [pendingExerciseEditData, setPendingExerciseEditData] = useState<CreateExerciseData | null>(null);
  const [exerciseEditMembersAffected, setExerciseEditMembersAffected] = useState(0);
  const [exerciseCompanionSheetOpen, setExerciseCompanionSheetOpen] = useState(false);
  const editingExerciseRef = useRef<Exercise | null>(null);
  const exerciseDrawerTriggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!editingExercise && !showExerciseEditWarningModal) {
      setExerciseCompanionSheetOpen(false);
    }
  }, [editingExercise, showExerciseEditWarningModal]);

  const openCreateExerciseDrawer = useCallback(() => {
    setCreateExerciseDrawerOpen(true);
  }, []);

  const closeCreateExerciseDrawer = useCallback(() => {
    setCreateExerciseDrawerOpen(false);
  }, []);

  const handleCreateExerciseFromDrawer = useCallback(
    async (exerciseData: CreateExerciseData): Promise<Exercise> => {
      return ExerciseService.createExercise(exerciseData);
    },
    [],
  );

  const handleCreateExercise = async (exerciseData: CreateExerciseData) => {
    setCreatingExercise(true);
    try {
      await ExerciseService.createExercise(exerciseData);
      ex.setLibExercisesPage(1);
      await loadAllExercisesForLibrary({ silent: true });
      setExerciseCompanionSheetOpen(false);
    } catch (error) {
      console.error('Error creating exercise:', error);
      const message =
        error instanceof Error ? error.message : 'Failed to create exercise. Please try again.';
      alert(message);
    } finally {
      setCreatingExercise(false);
    }
  };

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message;
    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as { message: unknown }).message);
    }
    return 'Failed to save changes.';
  };

  const performExerciseEdit = async (
    exerciseData: CreateExerciseData,
    updateMemberWorkouts: boolean,
  ) => {
    const exercise = editingExerciseRef.current ?? editingExercise;
    if (!exercise) return;
    setCreatingExercise(true);
    try {
      if (exercise.createdBy == null) {
        await ExerciseService.upsertExerciseOverride(exercise.id, exerciseData);
      } else {
        await ExerciseService.updateExercise(exercise.id, exerciseData);
      }
      if (updateMemberWorkouts) {
        await WorkoutService.updateSnapshotsForUpcomingSessions(exercise.id, {
          name: exerciseData.name,
          muscleGroup: exerciseData.muscleGroup,
          equipment: exerciseData.equipment,
          description: exerciseData.description,
          image: exerciseData.image,
          video: exerciseData.video,
          isPublic: exerciseData.isPublic,
        });
      }
      const exId = exercise.id;
      editingExerciseRef.current = null;
      setExerciseCompanionSheetOpen(false);
      setEditingExercise(null);
      setShowExerciseEditWarningModal(false);
      setPendingExerciseEditData(null);
      setExerciseEditMembersAffected(0);
      queueMicrotask(async () => {
        if (activeSection === 'library' && activeTab === 'exercises') {
          loadAllExercisesForLibrary({ silent: true }).catch((err) =>
            console.error('Refresh after exercise edit:', err),
          );
        }
        try {
          const refreshed = await ExerciseService.getExerciseById(exId);
          if (refreshed) {
            setViewingExercise((v) => (v?.id === exId ? refreshed : v));
          }
        } catch {
          /* ignore */
        }
      });
    } catch (error) {
      console.error('Error saving exercise:', error);
      alert(getErrorMessage(error));
    } finally {
      setCreatingExercise(false);
    }
  };

  const EXERCISE_USAGE_CHECK_TIMEOUT_MS = 8000;

  const handleSaveExerciseEdit = async (exerciseData: CreateExerciseData) => {
    const exercise = editingExerciseRef.current ?? editingExercise;
    if (!exercise) {
      setCreatingExercise(false);
      setExerciseCompanionSheetOpen(false);
      setEditingExercise(null);
      editingExerciseRef.current = null;
      return;
    }
    setCreatingExercise(true);
    try {
      let memberCount = 0;
      try {
        const result = await Promise.race([
          WorkoutService.getUpcomingSessionsUsingExercise(exercise.id),
          new Promise<{ sessionIds: string[]; memberCount: number }>((_, reject) =>
            setTimeout(() => reject(new Error('timeout')), EXERCISE_USAGE_CHECK_TIMEOUT_MS),
          ),
        ]);
        memberCount = result.memberCount;
      } catch (checkError) {
        console.warn('Exercise usage check failed or timed out, saving without member prompt:', checkError);
      }
      setCreatingExercise(false);
      if (memberCount > 0) {
        setPendingExerciseEditData(exerciseData);
        setExerciseEditMembersAffected(memberCount);
        setShowExerciseEditWarningModal(true);
        return;
      }
      await performExerciseEdit(exerciseData, false);
    } catch (error) {
      console.error('Error saving exercise:', error);
      alert(getErrorMessage(error));
    } finally {
      setCreatingExercise(false);
    }
  };

  const handleEditExerciseClickById = async (id: string) => {
    try {
      const fresh = await ExerciseService.getExerciseById(id);
      if (!fresh) return;
      editingExerciseRef.current = fresh;
      setEditingExercise(fresh);
      setExerciseCompanionSheetOpen(true);
    } catch (error) {
      console.error('Error fetching exercise for edit (by id):', error);
    }
  };

  const handleLibraryEditExercise = async (id: string) => {
    try {
      const fresh = await ExerciseService.getExerciseById(id);
      if (!fresh) return;
      setViewingExercise(fresh);
      setDrawerIsEdit(true);
    } catch (error) {
      console.error('Error fetching exercise for edit:', error);
    }
  };

  const handleViewExerciseListRow = async (row: TrainerExerciseListRow) => {
    try {
      const fresh = await ExerciseService.getExerciseById(row.id);
      if (fresh) {
        setViewingExercise(fresh);
        setDrawerIsEdit(false);
      }
    } catch (error) {
      console.error('Error fetching exercise for view (list row):', error);
    }
  };

  const closeExerciseDrawer = useCallback(() => {
    setViewingExercise(null);
    setDrawerIsEdit(false);
  }, []);

  const handleExerciseSaveFromDrawer = useCallback(
    async (data: CreateExerciseData): Promise<Exercise> => {
      const current = viewingExercise;
      if (!current) throw new Error('No exercise selected.');
      const isLibrary = current.createdBy == null || String(current.createdBy).trim() === '';
      if (isLibrary) {
        await ExerciseService.upsertExerciseOverride(current.id, data);
      } else {
        await ExerciseService.updateExercise(current.id, data);
      }
      const merged = await ExerciseService.getExerciseById(current.id);
      if (!merged) throw new Error('Could not reload exercise after save.');
      void loadAllExercisesForLibrary({ silent: true });
      return merged;
    },
    [viewingExercise, loadAllExercisesForLibrary],
  );

  const handleExerciseDiscardEdit = useCallback(async () => {
    const current = viewingExercise;
    if (!current) return;
    try {
      const fresh = await ExerciseService.getExerciseById(current.id);
      if (fresh) setViewingExercise(fresh);
    } catch (err) {
      console.error('Exercise edit discard refetch failed:', err);
    }
    setDrawerIsEdit(false);
  }, [viewingExercise]);

  useEffect(() => {
    if (!viewingExercise && !createExerciseDrawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [viewingExercise, createExerciseDrawerOpen]);

  useEffect(() => {
    if (!createExerciseDrawerOpen) return;
    let cancelled = false;
    ExerciseService.getDistinctExerciseTagValues()
      .then((opts) => {
        if (!cancelled) setExerciseTagOptions(opts);
      })
      .catch((err) => console.error('Distinct exercise tags (create):', err));
    return () => {
      cancelled = true;
    };
  }, [createExerciseDrawerOpen]);

  useEffect(() => {
    if (!drawerIsEdit || !viewingExercise) return;
    let cancelled = false;
    ExerciseService.getDistinctExerciseTagValues()
      .then((opts) => {
        if (!cancelled) setExerciseTagOptions(opts);
      })
      .catch((err) => console.error('Distinct exercise tags:', err));
    return () => {
      cancelled = true;
    };
  }, [drawerIsEdit, viewingExercise]);

  const shouldMountExerciseFormAtRoot =
    exerciseCompanionSheetOpen && !!editingExercise;

  const renderExerciseCompanionForm = (_inExerciseStack: boolean) => {
    if (!editingExercise || !exerciseCompanionSheetOpen) return null;
    return (
      <CreateExerciseForm
        initialData={{
          name: editingExercise.name,
          description: editingExercise.description,
          muscleGroup: editingExercise.muscleGroup,
          equipment: editingExercise.equipment,
          image: editingExercise.image,
          video: editingExercise.video,
          isPublic: editingExercise.isPublic,
        }}
        onSubmit={handleSaveExerciseEdit}
        onCancel={() => {
          editingExerciseRef.current = null;
          setEditingExercise(null);
          setShowExerciseEditWarningModal(false);
          setPendingExerciseEditData(null);
          setExerciseCompanionSheetOpen(false);
        }}
        isLoading={creatingExercise}
        title="Edit Exercise"
        submitLabel="Save"
        loadingLabel="Saving..."
        isLibraryOverride={editingExercise.createdBy == null}
        overlayZClassName="z-[125]"
        embedInExerciseOverlay={false}
        embeddedInWorkoutExerciseStack={false}
      />
    );
  };

  return {
    createExerciseDrawerOpen,
    setCreateExerciseDrawerOpen,
    creatingExercise,
    handleCreateExercise,
    editingExercise,
    setEditingExercise,
    viewingExercise,
    setViewingExercise,
    drawerIsEdit,
    setDrawerIsEdit,
    exerciseTagOptions,
    savedToastExerciseName,
    setSavedToastExerciseName,
    savedToastVariant,
    setSavedToastVariant,
    exerciseEditDrawerRef,
    createExerciseDrawerRef,
    showExerciseEditWarningModal,
    setShowExerciseEditWarningModal,
    pendingExerciseEditData,
    setPendingExerciseEditData,
    exerciseEditMembersAffected,
    exerciseCompanionSheetOpen,
    setExerciseCompanionSheetOpen,
    editingExerciseRef,
    exerciseDrawerTriggerRef,
    openCreateExerciseDrawer,
    closeCreateExerciseDrawer,
    handleCreateExerciseFromDrawer,
    handleEditExerciseClickById,
    handleLibraryEditExercise,
    handleViewExerciseListRow,
    closeExerciseDrawer,
    handleExerciseSaveFromDrawer,
    handleExerciseDiscardEdit,
    performExerciseEdit,
    shouldMountExerciseFormAtRoot,
    renderExerciseCompanionForm,
  };
}
